import "./style.css";
import { initAssets, loadBundles } from "./engine/assets";
import { Engine } from "./engine/engine";
import { LoadingScene } from "./engine/LoadingScene";
import { RemoteContent } from "./engine/remoteContent";
import { rulesFromConfig } from "./game/core/rules";
import { fromSave, toSave } from "./game/core/save";
import { defaultRules, type TapRules, type TapState } from "./game/core/tap";
import { CONTENT_BUNDLE, gameTextures, manifest } from "./game/view/assets";
import { connectionText } from "./game/view/connectionText";
import { MainScene } from "./game/view/MainScene";
import { Analytics } from "./platform/analytics";
import { loadAppConfig } from "./platform/appConfig";
import { getClientInfo } from "./platform/clientInfo";
import { envStore } from "./platform/envStore";
import { onActiveChange, ready } from "./platform/native";
import { EnvPin, withEnv } from "./platform/net/env";
import type { Content } from "./platform/net/protocol";
import { ServerSave } from "./platform/net/serverSave";
import { Session } from "./platform/net/session";
import { GameSocket } from "./platform/net/socket";
import { deviceId } from "./platform/deviceId";
import { loadJson, saveJson } from "./platform/storage";

const SAVE_KEY = "save";
const SAVE_DELAY_MS = 1000;
/** Used when app-config.json is missing or invalid. */
const DEFAULT_WS_URL = "ws://localhost:8080/v1/ws";
/** Spreads config.get calls after a publish so clients don't all fetch at once. */
const CONFIG_REFRESH_JITTER_MS = 3000;
/** The first content download holds the game back at most this long; then the built-in art is used. */
const CONTENT_TIMEOUT_MS = 10_000;
/** Share of the loading bar for the built-in assets; OTA content fills the rest. */
const LOCAL_ASSETS_PROGRESS = 0.5;

let pendingSave: TapState | undefined;
let saveTimer: ReturnType<typeof setTimeout> | undefined;
/** The server copy of the save (state.save); set once the socket exists. */
let serverSave: ServerSave | undefined;

/** Batches frequent state changes into one write per second. */
function scheduleSave(state: TapState): void {
  pendingSave = state;
  saveTimer ??= setTimeout(flushSave, SAVE_DELAY_MS);
}

function flushSave(): void {
  clearTimeout(saveTimer);
  saveTimer = undefined;
  if (!pendingSave) return;
  const data = toSave(pendingSave);
  void saveJson(SAVE_KEY, data).catch((err: unknown) => console.error("save failed", err));
  serverSave?.save({ ...data });
  pendingSave = undefined;
}

async function boot(): Promise<void> {
  const container = document.getElementById("game");
  if (!container) throw new Error("#game element missing");
  const bootStart = performance.now();

  const engine = await Engine.create(container);
  const loading = new LoadingScene();
  engine.setScene(loading);

  const [appConfig, clientInfo] = await Promise.all([loadAppConfig({ wsUrl: DEFAULT_WS_URL }), getClientInfo()]);
  const wsUrl = import.meta.env.VITE_BACKEND_WS_URL || appConfig.wsUrl;
  const env = new EnvPin(envStore, appConfig.env);
  await env.load();
  const socket = new GameSocket({ url: () => withEnv(wsUrl, env.current) });
  const session = new Session(socket, deviceId, clientInfo, env);
  const analytics = new Analytics(socket, session);
  analytics.track("app_open", { cold_start: true });

  let scene: MainScene | undefined;
  let rules: TapRules = defaultRules;
  let configVersion: number | undefined;
  const applyConfig = ({ version, data }: { version: number; data: unknown }) => {
    rules = rulesFromConfig(data);
    scene?.setRules(rules);
    if (version !== configVersion) analytics.track("config_applied", { version });
    configVersion = version;
  };

  // Content changes run one at a time, always towards the newest content seen.
  const content = new RemoteContent();
  let latestContent: Content | undefined;
  let contentQueue = Promise.resolve();
  const syncContent = () => {
    contentQueue = contentQueue
      .then(async () => {
        const changed = content.update(latestContent);
        if (changed.includes(CONTENT_BUNDLE) || content.needsLoad(CONTENT_BUNDLE)) {
          const loaded = await content.load(CONTENT_BUNDLE);
          scene?.setTextures(gameTextures(loaded));
          console.info(`content: ${loaded ? "applied" : "using built-in art for"} bundle ${CONTENT_BUNDLE}`);
        }
        // Only after the swap, so the scene never shows a destroyed texture.
        await content.release();
      })
      .catch((err: unknown) => console.error("content update failed", err));
  };

  session.on("state", (state) => {
    if (scene) scene.setConnection(state.kind === "ready", connectionText(state));
    else loading.setStatus(connectionText(state));
  });
  serverSave = new ServerSave(socket);
  let serverState: { version: number; data: unknown } | undefined;
  const firstReady = new Promise<void>((resolve) => session.on("ready", () => resolve()));
  session.on("ready", (bootstrap) => {
    serverSave?.setVersion(bootstrap.state.version);
    serverState ??= bootstrap.state;
    applyConfig(bootstrap.config);
    latestContent = bootstrap.config.content;
    if (scene) syncContent();
  });
  // A real game waits for a safe point (e.g. between levels) before refreshing; here it is right away.
  session.on("configPending", () => {
    setTimeout(() => {
      session.refreshConfig().catch((err: unknown) => console.warn("config refresh failed", err));
    }, Math.random() * CONFIG_REFRESH_JITTER_MS);
  });
  session.on("config", (config) => {
    applyConfig(config);
    latestContent = config.content;
    if (scene) syncContent();
  });

  onActiveChange((active) => {
    analytics.track(active ? "app_foreground" : "app_background");
    // Before the session pauses, so the batch still goes out on this connection.
    analytics.setActive(active);
    engine.setActive(active);
    session.setActive(active);
    if (!active) flushSave();
  });
  await ready();
  session.start();

  await initAssets(manifest);
  const [saved] = await Promise.all([
    loadJson(SAVE_KEY),
    loadBundles("main", (progress) => loading.setProgress(progress * LOCAL_ASSETS_PROGRESS)),
  ]);
  await firstReady;
  // Never blocked by the CDN: on errors or timeout the game starts with the built-in art.
  const firstContent = latestContent;
  content.update(firstContent);
  const remote = await content.load(
    CONTENT_BUNDLE,
    (progress) => loading.setProgress(LOCAL_ASSETS_PROGRESS + progress * (1 - LOCAL_ASSETS_PROGRESS)),
    CONTENT_TIMEOUT_MS,
  );
  loading.setProgress(1);
  console.info(`content: ${remote ? "loaded" : "using built-in art for"} bundle ${CONTENT_BUNDLE}`);
  // The server's save wins once it has one; the local save is the offline fallback.
  const start = serverState && serverState.version > 0 ? serverState.data : saved;
  scene = new MainScene(fromSave(start), rules, gameTextures(remote), scheduleSave, (earned, state) =>
    analytics.track("tap", { coins: earned, total: state.coins }),
  );
  engine.setScene(scene);
  analytics.track("game_ready", { load_ms: Math.round(performance.now() - bootStart), content: remote ? "remote" : "built_in" });
  // Content that arrived during the first download.
  if (latestContent !== firstContent) syncContent();
}

boot().catch((err: unknown) => {
  console.error("boot failed", err);
  // Never leave the native splash screen stuck on top of an error.
  void ready();
});
